import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileSignature, Download, Loader2, Plus, CheckCircle, XCircle, Clock } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A', teal: '#00B4D8' };
const TYPES = [
  ['sum_insured_change', 'Sum insured change'], ['premium_adjustment', 'Premium adjustment'],
  ['cover_extension', 'Cover extension'], ['address_change', 'Address change'],
  ['interested_party', 'Interested party update'], ['other', 'Other'],
];
const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EndorsementsCard({ policy, onIssued }) {
  const api = useApi();
  const { user } = useAuth();
  const isStaff = ['underwriter', 'mga_admin', 'platform_owner'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: 'sum_insured_change', description: '', effective_date: new Date().toISOString().slice(0, 10), new_annual_premium: '', new_sum_insured: '' });
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const si = parseFloat(form.new_sum_insured);
    if (!showForm || !si || si <= 0 || form.new_annual_premium) { setPreview(null); return; }
    const t = setTimeout(() => {
      api.post(`/policies/${policy.policy_id}/endorsements/reprice`, {
        new_sum_insured: si, effective_date: form.effective_date,
      }).then(r => setPreview(r.data)).catch(() => setPreview(null));
    }, 600);
    return () => clearTimeout(t);
  }, [form.new_sum_insured, form.new_annual_premium, form.effective_date, showForm, api, policy.policy_id]);

  const load = useCallback(() => {
    api.get(`/policies/${policy.policy_id}/endorsements`).then(r => setItems(r.data.endorsements || [])).catch(() => {});
  }, [api, policy.policy_id]);
  useEffect(() => { load(); }, [load]);

  const issue = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/policies/${policy.policy_id}/endorsements`, {
        type: form.type, description: form.description,
        effective_date: form.effective_date,
        new_annual_premium: form.new_annual_premium ? parseFloat(form.new_annual_premium) : null,
        new_sum_insured: form.new_sum_insured ? parseFloat(form.new_sum_insured) : null,
      });
      const adj = data.premium_adjustment || 0;
      if (data.status === 'pending_approval') {
        toast.warning(`${data.endorsement_number} requires second-underwriter sign-off before it binds (adjustment ${adj > 0 ? '+' : ''}$${Math.abs(adj).toLocaleString()})`);
      } else {
        toast.success(`${data.endorsement_number} issued${adj ? ` — ${adj > 0 ? 'additional premium' : 'credit'} $${Math.abs(adj).toLocaleString()}${data.invoice_number ? ` (${data.invoice_number})` : ''}` : ''}`);
      }
      setShowForm(false);
      setForm({ type: 'sum_insured_change', description: '', effective_date: new Date().toISOString().slice(0, 10), new_annual_premium: '', new_sum_insured: '' });
      load();
      onIssued?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to issue endorsement');
    }
    setBusy(false);
  };

  const decide = async (endorsementId, action, number) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/endorsements/${endorsementId}/${action}`,
        action === 'reject' ? { reason: 'Second-underwriter review — adjustment declined' } : {});
      toast.success(action === 'approve'
        ? `${number} approved and bound${data.invoice_number ? ` — invoice ${data.invoice_number} raised` : ''}`
        : `${number} rejected`);
      load();
      onIssued?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
    setBusy(false);
  };

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="endorsements-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
          <FileSignature className="w-3.5 h-3.5" style={{ color: C.blue }} />Endorsements ({items.length})
        </h3>
        {isStaff && policy.status === 'active' && (
          <button onClick={() => setShowForm(v => !v)} data-testid="new-endorsement-btn"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: C.blue }}>
            <Plus className="w-3 h-3" />{showForm ? 'Close' : 'New Endorsement'}
          </button>
        )}
      </div>

      {showForm && (
        <div className="p-3 mb-3 rounded-sm border space-y-2.5" style={{ borderColor: 'rgba(26,76,124,0.25)', background: 'rgba(26,76,124,0.03)' }}
          data-testid="endorsement-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Type</p>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                data-testid="endorsement-type" className="w-full px-2 py-1.5 border rounded-sm text-xs bg-white" style={{ borderColor: 'rgba(136,150,168,0.3)' }}>
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Effective date</p>
              <input type="date" value={form.effective_date} onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))}
                data-testid="endorsement-effective-date" className="w-full px-2 py-1.5 border rounded-sm text-xs" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Revised annual premium (optional)</p>
              <input type="text" inputMode="decimal" value={form.new_annual_premium}
                onChange={e => setForm(f => ({ ...f, new_annual_premium: e.target.value.replace(/[^\d.]/g, '') }))}
                data-testid="endorsement-new-premium" placeholder={`current $${(policy.annual_premium || 0).toLocaleString()}`}
                className="w-full px-2 py-1.5 border rounded-sm text-xs font-mono" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Revised sum insured (optional)</p>
              <input type="text" inputMode="decimal" value={form.new_sum_insured}
                onChange={e => setForm(f => ({ ...f, new_sum_insured: e.target.value.replace(/[^\d.]/g, '') }))}
                data-testid="endorsement-new-si" placeholder={`current $${(policy.sum_insured || 0).toLocaleString()}`}
                className="w-full px-2 py-1.5 border rounded-sm text-xs font-mono" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            </div>
            <div className="md:col-span-2">
              <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Endorsement wording *</p>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
                data-testid="endorsement-description" placeholder="e.g. Sum insured increased following acquisition of new premises at…"
                className="w-full px-2 py-1.5 border rounded-sm text-xs" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            </div>
          </div>
          {preview && !form.new_annual_premium && (
            <div className="p-2.5 rounded-sm border" data-testid="mta-reprice-preview"
              style={{ borderColor: 'rgba(0,180,216,0.35)', background: 'rgba(0,180,216,0.05)' }}>
              <p className="text-[10px]" style={{ color: C.navy }}>
                <b>ARIA reprice</b> ({preview.method === 'engine' ? 'rated through the MARAIS engine' : 'pro-rata sum-insured scaling'}):
                new annual premium <b className="font-mono" data-testid="mta-reprice-premium">${(preview.new_annual_premium || 0).toLocaleString()}</b>
                {' '}({preview.delta_annual >= 0 ? '+' : '−'}${Math.abs(preview.delta_annual || 0).toLocaleString()}/yr)
                {' '}→ {preview.pro_rata_adjustment >= 0 ? 'additional premium' : 'credit'} <b className="font-mono">${Math.abs(preview.pro_rata_adjustment || 0).toLocaleString()}</b>
                {' '}for the {preview.days_remaining} days remaining. Leave the premium field blank to issue at this figure, or override it above.
              </p>
            </div>
          )}
          <p className="text-[10px]" style={{ color: C.slate }}>
            A premium change is adjusted <b>pro-rata for the days remaining to expiry</b> — an additional-premium invoice or credit note is raised automatically. Adjustments over <b>$1,000</b> (or ±20% of annual premium) require a <b>second-underwriter sign-off</b> before binding.
          </p>
          <button onClick={issue} disabled={busy} data-testid="issue-endorsement-btn"
            className="flex items-center gap-1 px-4 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ background: C.green }}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSignature className="w-3 h-3" />}
            Issue Endorsement
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-xs" style={{ color: C.slate }}>No endorsements on this policy.</p>
      ) : (
        <div className="space-y-1.5" data-testid="endorsements-list">
          {items.slice(0, 8).map(e => (
            <div key={e.endorsement_id} className="py-1.5 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.06)' }}
              data-testid={`endorsement-row-${e.endorsement_number}`}>
              <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs">
                  <span className="font-mono font-medium" style={{ color: C.blue }}>{e.endorsement_number}</span>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/endorsement/${e.endorsement_id}`} target="_blank" rel="noreferrer"
                    data-testid={`endorsement-pdf-${e.endorsement_number}`} className="ml-2 text-[10px] font-semibold hover:underline" style={{ color: C.teal }}
                    title="Endorsement certificate (PDF)">PDF</a>
                  <span className="ml-2 capitalize" style={{ color: C.slate }}>{(e.type || '').replace(/_/g, ' ')}</span>
                  {e.status === 'pending_approval' && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: 'rgba(232,160,32,0.12)', color: C.amber }}
                      data-testid={`endorsement-pending-${e.endorsement_number}`}>
                      <Clock className="w-2.5 h-2.5 inline mr-0.5" />Awaiting sign-off
                    </span>
                  )}
                  {e.status === 'rejected' && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: 'rgba(226,75,74,0.1)', color: C.red }}>Rejected</span>
                  )}
                </p>
                <p className="text-[10px] truncate" style={{ color: C.slate }}>{e.description}</p>
              </div>
              {e.premium_adjustment ? (
                <span className="text-xs font-mono font-semibold" style={{ color: e.premium_adjustment > 0 ? C.navy : C.green }}>
                  {e.premium_adjustment > 0 ? '+' : '−'}${Math.abs(e.premium_adjustment).toLocaleString()}
                </span>
              ) : null}
              <span className="text-[10px] font-mono" style={{ color: C.slate }}>{String(e.effective_date || '').slice(0, 10)}</span>
              <a href={`${API_BASE}/endorsements/${e.endorsement_id}/document`} target="_blank" rel="noreferrer"
                data-testid={`endorsement-doc-${e.endorsement_number}`}
                className="p-1.5 rounded hover:bg-blue-50 transition-colors" title="Download endorsement schedule">
                <Download className="w-3.5 h-3.5" style={{ color: C.blue }} />
              </a>
              </div>
              {e.status === 'pending_approval' && isStaff && (
                user?.name === e.issued_by ? (
                  <p className="text-[10px] italic mt-1" style={{ color: C.slate }}>
                    Issued by you — a second underwriter must approve or reject this endorsement.
                  </p>
                ) : (
                  <div className="flex gap-2 mt-1.5">
                    <button onClick={() => decide(e.endorsement_id, 'approve', e.endorsement_number)} disabled={busy}
                      data-testid={`approve-endorsement-${e.endorsement_number}`}
                      className="flex items-center gap-1 px-3 py-1 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                      style={{ background: C.green }}>
                      <CheckCircle className="w-3 h-3" />Approve &amp; Bind
                    </button>
                    <button onClick={() => decide(e.endorsement_id, 'reject', e.endorsement_number)} disabled={busy}
                      data-testid={`reject-endorsement-${e.endorsement_number}`}
                      className="flex items-center gap-1 px-3 py-1 rounded-sm border text-[10px] font-semibold transition-colors hover:bg-red-50 disabled:opacity-50"
                      style={{ color: C.red, borderColor: 'rgba(226,75,74,0.3)' }}>
                      <XCircle className="w-3 h-3" />Reject
                    </button>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
